import classNames from 'classnames';
import useCallbackRef from 'hooks/useCallbackRef';
import React, { forwardRef, useCallback, useImperativeHandle } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import { NotesList } from '../../subrouters/Seller/subrouters/ListingDetailSeller/components/NotesList';
import useInfinitePagination from 'hooks/useInfinitePagination';
import { Note, NoteFile, NoteTag } from 'models/Note';
import useQueryParams from 'hooks/useQueryParams';
import api from 'api';
import { getSellingPortfolio } from 'utils';
import { DealRoom } from 'models/DealRoom';
import { AxiosRequestConfig } from 'axios';
import { DropzoneFile } from 'components/Dropzone/utils';
import { ReactionType } from 'models/Reactions';
import { updateNoteReaction } from '../../subrouters/Seller/subrouters/ListingDetailSeller/components/NotesList/utils';

import './DealNotes.styles.scss';

type DealNotesProps = {
  className?: string;
  dealRoomId: string;
  dealRoomName: string;
  updateDealRoom?: (dealRoom: Partial<DealRoom>) => void;
};

const DealNotes: React.ForwardRefRenderFunction<
  SideDrawerRef,
  DealNotesProps
> = ({ className, dealRoomId, dealRoomName, updateDealRoom }, ref) => {
  const classes = classNames('deal-notes-drawer', className);
  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);

  useImperativeHandle(ref, () => drawer, [drawer]);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    params: { newNote },
  } = useQueryParams();

  const isOpen = drawer?.open;

  const {
    loading,
    items: notes,
    setItems: setNotes,
    onContainerScrolled: onNotesScroll,
  } = useInfinitePagination<Note>({
    condition: !!(dealRoomId && isOpen),
    resetDeps: [dealRoomId, isOpen],
    dependencies: [dealRoomId, isOpen],
    makeRequest: async (currentPage: number) =>
      api.dealRoom
        .getDealNotes(dealRoomId, {
          $page: currentPage.toString(),
          $limit: '200',
        })
        .then((res) => res.data),
  });

  const handleOnSendNote = useCallback(
    async (
      content: string,
      extractedTags: NoteTag[],
      uploadedFiles: DropzoneFile[],
    ) => {
      const { data: sentNote } = await api.dealRoom.sendDealNote(
        dealRoomId,
        content,
        extractedTags,
        uploadedFiles,
      );

      setNotes((prev) => [sentNote, ...prev]);
      updateDealRoom?.({ unreadNotesCount: 0, unrepliedNotesCount: 0 });
    },
    [dealRoomId, setNotes, updateDealRoom],
  );

  const removeNote = useCallback(
    async (noteId: string) => {
      await api.dealRoom.removeDealNote(noteId);

      setNotes((oldNotes) => {
        const newNotes = oldNotes.filter(({ _id }) => _id !== noteId);

        if (!newNotes.length) {
          updateDealRoom({
            unreadNotesCount: 0,
            unrepliedNotesCount: 0,
          });
        }

        return newNotes;
      });
    },
    [setNotes, updateDealRoom],
  );

  const onClose = useCallback(() => {
    if (newNote) {
      navigate(getSellingPortfolio(dealRoomId, 'deal'));
    }
  }, [dealRoomId, navigate, newNote]);

  const onUploadReq = useCallback(
    (file: File, config: AxiosRequestConfig) =>
      api.dealRoom.uploadDealNoteFile(dealRoomId, file, config),
    [dealRoomId],
  );

  const onFilePreview = useCallback(
    (fileId: string, noteId: string) =>
      api.dealRoom.previewNoteFile(noteId, fileId),
    [],
  );

  const removeNoteFile = useCallback(
    (fileId: string) =>
      setNotes((old) =>
        old.map((note) => ({
          ...note,
          files: note.files.filter(({ _id }) => _id !== fileId),
        })),
      ),
    [setNotes],
  );

  const updateNoteFile = useCallback(
    (updated: Partial<NoteFile>) =>
      setNotes((old) =>
        old.map((note) => ({
          ...note,
          files: note.files.map((file) =>
            file._id === updated._id ? { ...file, ...updated } : file,
          ),
        })),
      ),
    [setNotes],
  );

  const onFileRemove = useCallback(
    async (fileId: string, removeFromDataRoom: boolean, noteId: string) => {
      await api.dealRoom.removeNoteFile(noteId, fileId, removeFromDataRoom);
      removeNoteFile(fileId);
    },
    [removeNoteFile],
  );

  const onFileAddToDataRoom = useCallback(
    async (fileId: string, noteId: string) => {
      await api.dealRoom.moveFileToDataRoom(noteId, fileId);
      updateNoteFile({ _id: fileId, isDataRoomFile: true });
    },
    [updateNoteFile],
  );

  const onReact = useCallback(
    (noteId: string, reaction: ReactionType) => {
      api.dealRoom.addNoteReaction(noteId, reaction);
      setNotes((old) => updateNoteReaction(old, noteId, reaction));
    },
    [setNotes],
  );

  return (
    <SideDrawer ref={drawerRef} className={classes} closeAction={onClose}>
      <p className="drawer-title">{t('dealNotes')}</p>

      <NotesList
        loading={loading}
        notes={notes}
        notesAbout={dealRoomName}
        emptyMessage={t('createFirstDealNote')}
        handleOnSendNote={handleOnSendNote}
        removeNote={removeNote}
        onNotesScroll={onNotesScroll}
        onUploadReq={onUploadReq}
        onFilePreview={onFilePreview}
        onFileRemove={onFileRemove}
        onFileAddToDataRoom={onFileAddToDataRoom}
        onReact={onReact}
        getReactionUsers={api.dealRoom.getNoteReactions}
      />
    </SideDrawer>
  );
};

export default forwardRef(DealNotes);
